import React from 'react';
import { Target, Users, TrendingUp, Shield, Globe, Zap, BarChart3, CheckCircle2, ArrowRight, Building, Landmark, Heart, BookOpen, Cpu, DollarSign, Clock, FileText, Scale } from 'lucide-react';
import KineticHero from '@/components/KineticHero';

function UseCasesPage() {
  return (
    <div data-testid="usecases-page" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="ucase"
        palette="emerald"
        eyebrow="White paper · strategic value"
        title="Use Cases &"
        accent="Strategic Value"
        subtitle="How Sovereign IQ transforms country intelligence for investment professionals, governments, and development institutions."
      />
      <div className="p-6 lg:p-8">

      <div className="max-w-[1100px] space-y-8">
        {/* Executive Overview */}
        <section className="bg-[#1B2A3D] text-white rounded-xl shadow-sm p-8">
          <h2 className="text-xl font-bold tracking-tight mb-4">Executive Overview</h2>
          <p className="text-sm text-white/80 leading-relaxed mb-6">
            Sovereign IQ eliminates the traditional 6-12 week timeline and $50,000-$200,000 cost of producing a single country market entry report. 
            Using AI-powered analysis from our AI intelligence engine — configured as a senior partner economist with 50 years of experience — the platform generates 
            comprehensive, 12-section, investment-grade country blueprints in minutes, not months.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Time to Report', before: '6-12 weeks', after: 'Minutes', icon: Clock },
              { label: 'Cost per Report', before: '$50K-$200K', after: 'Near zero', icon: DollarSign },
              { label: 'Data Sources', before: '5-10 manual', after: '1,000+ automated', icon: Globe },
              { label: 'Sectors Covered', before: '1 per engagement', after: '115 simultaneously', icon: BarChart3 },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/5 p-4 rounded-lg">
                  <Icon className="w-5 h-5 text-[#00A651] mb-2" />
                  <span className="text-[9px] text-white/40 uppercase tracking-widest">{item.label}</span>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-white/40 line-through">{item.before}</span>
                    <ArrowRight className="w-3 h-3 text-[#00A651]" />
                    <span className="text-sm font-bold text-[#00A651]">{item.after}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* The Problem We Solve */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#00A651]" /> The Problem We Solve
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: 'Fragmented Intelligence', desc: 'Investment teams spend weeks gathering data from World Bank, IMF, credit agencies, trade databases, and local sources — then manually synthesizing it into a coherent picture. By the time the report is ready, the data is already stale.' },
              { title: 'Prohibitive Cost', desc: 'A single country market entry study from a top-tier strategy firm or a Big 4 firm costs $50K-$200K. Most organizations can only afford to analyze 2-3 countries per year, missing opportunities in the other 190.' },
              { title: 'Sector Blind Spots', desc: 'Traditional reports are sector-specific. An energy company analyzing Nigeria gets no insight into that same country\'s fintech opportunity. Sovereign IQ generates sector-independent AND sector-specific analysis simultaneously.' },
              { title: 'No Real-Time Updates', desc: 'Static PDF reports become outdated within weeks. Economic indicators shift, governments change, currencies fluctuate. Sovereign IQ integrates live World Bank data that refreshes automatically.' },
            ].map((item, i) => (
              <div key={i} className="p-4 bg-[#f8f9ff] rounded-lg border-l-3 border-l-[#ef4444]" style={{ borderLeft: '3px solid #ef4444' }}>
                <h3 className="text-sm font-bold text-[#0b1c30] mb-2">{item.title}</h3>
                <p className="text-xs text-[#434655] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases by Stakeholder */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#00A651]" /> Use Cases by Stakeholder
          </h2>

          {/* Private Equity & Investment Funds */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#004ac6] flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#0b1c30]">Private Equity & Investment Funds</h3>
                <p className="text-xs text-[#434655]">Deal sourcing, due diligence, portfolio monitoring</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-13">
              {[
                { title: 'Pre-Deal Screening', desc: 'Screen 20+ countries in a day before committing to deep due diligence. Compare GDP growth, FDI flows, credit ratings, and governance scores side-by-side to shortlist the top 3-5 targets.' },
                { title: 'Investment Committee Briefings', desc: 'Generate investment-grade PDFs (30-40 pages) with charts, risk matrices, and financial projections — ready for IC presentation without weeks of analyst time.' },
                { title: 'Portfolio Country Monitoring', desc: 'Track macroeconomic trends (inflation, unemployment, debt/GDP) across all portfolio countries in real-time via the Intelligence Feed and trend charts. Get early warnings before crises hit.' },
              ].map((uc, i) => (
                <div key={i} className="p-4 bg-[#eff4ff] rounded-lg">
                  <h4 className="text-xs font-bold text-[#004ac6] mb-2">{uc.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Development Finance Institutions */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#00A651] flex items-center justify-center">
                <Landmark className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#0b1c30]">Development Finance Institutions (DFIs)</h3>
                <p className="text-xs text-[#434655]">IFC, AfDB, EBRD, CDC, FMO, DFC, and bilateral agencies</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-13">
              {[
                { title: 'Country Strategy Papers', desc: 'Auto-generate the analytical backbone of 5-year Country Strategy Papers covering macro stability, governance quality, sector opportunities, and SDG alignment — reducing drafting time from 3 months to 3 days.' },
                { title: 'ESG & Impact Assessment', desc: 'The Social Impact section (Section 10) covers employment creation, community development, SDG alignment, and ESG scoring — critical for DFI mandates. The Governance section (11) maps institutional quality and PPP readiness.' },
                { title: 'Sovereign Risk Dashboards', desc: 'Monitor credit ratings, fragility scores, and debt sustainability across entire portfolios. The interactive map provides geographic visualization of exposure with real-time World Bank data.' },
              ].map((uc, i) => (
                <div key={i} className="p-4 bg-green-50 rounded-lg">
                  <h4 className="text-xs font-bold text-[#00A651] mb-2">{uc.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Multinational Corporations */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#f59e0b] flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#0b1c30]">Multinational Corporations</h3>
                <p className="text-xs text-[#434655]">Market entry strategy, expansion planning, supply chain diversification</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-13">
              {[
                { title: 'Market Entry Analysis', desc: 'The PESTLE, Market Opportunity, and Competitive Landscape sections provide the exact analysis a CEO needs to decide whether to enter a new market. The Business Model section recommends entry strategies and phased rollout plans.' },
                { title: 'Supply Chain Risk', desc: 'Evaluate alternative manufacturing or sourcing countries across 115 sectors. Compare Governance quality, trade openness, infrastructure scores, and regulatory frameworks to de-risk supply chains.' },
                { title: 'Board-Ready Presentations', desc: 'The AI Studio generates professional video briefings (Sora 2), audio narrations (OpenAI TTS), and infographics (Gemini) — ready for board meetings, investor days, or regional strategy sessions.' },
              ].map((uc, i) => (
                <div key={i} className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="text-xs font-bold text-[#f59e0b] mb-2">{uc.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Government & Sovereign Wealth */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#8b5cf6] flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#0b1c30]">Government Agencies & Sovereign Wealth Funds</h3>
                <p className="text-xs text-[#434655]">Investment promotion, FDI attraction, bilateral negotiations</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-13">
              {[
                { title: 'Investment Promotion', desc: 'Generate sector-specific country profiles to share with potential foreign investors. Show exactly why your country is attractive for renewable energy, fintech, agriculture, or any of 115 sectors.' },
                { title: 'Bilateral Trade Analysis', desc: 'Compare two countries side-by-side using the Comparison view to identify complementary trade opportunities, investment gaps, and areas for bilateral agreements.' },
                { title: 'Peer Benchmarking', desc: 'Understand how your country ranks against regional peers on governance, ease of business, credit ratings, and economic indicators. Use the trend charts to show improvement trajectories.' },
              ].map((uc, i) => (
                <div key={i} className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="text-xs font-bold text-[#8b5cf6] mb-2">{uc.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Consulting Firms & Advisory */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#0b1c30] flex items-center justify-center">
                <Cpu className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#0b1c30]">Consulting Firms & Advisory Practices</h3>
                <p className="text-xs text-[#434655]">Top-tier strategy & boutique advisory</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-13">
              {[
                { title: 'Accelerated Research Phase', desc: 'What takes a team of 3 analysts 4 weeks to produce, Sovereign IQ generates in minutes. Use the output as a first draft, then layer in proprietary insights and client-specific context.' },
                { title: 'Pitch Deck Intelligence', desc: 'Win new mandates by including data-rich country analysis in pitches. Generate blueprints for prospective client markets to demonstrate deep understanding before the engagement even starts.' },
                { title: 'Knowledge Management', desc: 'Build a living library of country intelligence across all active and potential markets. When a partner asks "What do we know about Tanzania\'s mining sector?" — the answer is instantly available.' },
              ].map((uc, i) => (
                <div key={i} className="p-4 bg-[#f8f9ff] rounded-lg">
                  <h4 className="text-xs font-bold text-[#0b1c30] mb-2">{uc.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Academic & Research */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#059669] flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#0b1c30]">Academic Institutions & Research Centers</h3>
                <p className="text-xs text-[#434655]">Universities, think tanks, policy research</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-13">
              {[
                { title: 'Teaching & Case Studies', desc: 'Generate real-world country analysis for MBA courses, development economics classes, and political science programs. Students can compare their manual analysis against AI-generated institutional-grade output.' },
                { title: 'Policy Research', desc: 'Rapidly assess the economic landscape of countries for policy papers. The 1,000+ data sources and numbered references provide a solid foundation for academic citation.' },
                { title: 'Cross-Country Studies', desc: 'Use the comparison view to analyze patterns across 5+ countries simultaneously — governance quality vs GDP growth, infrastructure investment vs FDI attraction, etc.' },
              ].map((uc, i) => (
                <div key={i} className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="text-xs font-bold text-[#059669] mb-2">{uc.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry-Specific Value */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#00A651]" /> Industry-Specific Value
          </h2>
          <p className="text-xs text-[#434655] mb-5">Sovereign IQ covers 115 sectors. Here's how specific industries benefit:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { sector: 'Renewable Energy', value: 'Analyze grid infrastructure, energy mix, IRENA capacity data, NDC commitments, carbon legislation, and government subsidies across target countries. Identify where solar/wind ROI is highest.' },
              { sector: 'Healthcare & Pharma', value: 'Map disease burden, healthcare spending/GDP, WHO indicators, regulatory frameworks (FDA equivalents), IP protection, and distribution infrastructure. Critical for market access strategies.' },
              { sector: 'Financial Services & FinTech', value: 'Assess financial inclusion (World Bank Findex), mobile penetration (GSMA), central bank digital currency plans, regulatory sandbox availability, and banking sector competitiveness.' },
              { sector: 'Agriculture & Agribusiness', value: 'Evaluate arable land, water resources, climate vulnerability, trade agreements (AfCFTA), cold chain infrastructure, and government subsidy programs. Compare export competitiveness across corridors.' },
              { sector: 'Mining & Extractive', value: 'Analyze geological potential, mining codes, royalty structures, environmental regulations, community relations risk, and infrastructure (ports, rail, power) for resource extraction.' },
              { sector: 'Telecommunications', value: 'Map spectrum allocation, regulatory frameworks, mobile penetration, fiber deployment, tower infrastructure, and digital economy policies. Identify markets ripe for 5G investment.' },
              { sector: 'Construction & Real Estate', value: 'Assess urbanization rates, housing deficits, building codes, cement/steel availability, labor costs, and PPP frameworks for infrastructure projects.' },
              { sector: 'Education & EdTech', value: 'Evaluate literacy rates, STEM graduation numbers, internet penetration, government education spending, and regulatory frameworks for private and digital education.' },
            ].map((item, i) => (
              <div key={i} className="p-4 bg-[#f8f9ff] rounded-lg">
                <h4 className="text-xs font-bold text-[#004ac6] mb-2">{item.sector}</h4>
                <p className="text-[11px] text-[#434655] leading-relaxed">{item.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Platform Advantages */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#00A651]" /> Platform Advantages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Zap, title: 'Speed', desc: '12-section blueprint generated in ~90 minutes. What takes a consulting team weeks, Sovereign IQ delivers in a single working session.', metric: '100x faster' },
              { icon: DollarSign, title: 'Cost Efficiency', desc: 'Eliminate $50K-$200K per country report. Analyze 50 countries for less than the cost of one traditional study.', metric: '99% cheaper' },
              { icon: Globe, title: '1,000+ Data Sources', desc: 'Automatically references World Bank, IMF, UNCTAD, Fitch, Moody\'s, S&P, IEA, IRENA, ILO, UNDP, ACLED, GDELT, and 980+ more across 20 categories with 145 API integrations.', metric: '500+ sources' },
              { icon: BarChart3, title: 'Live Data', desc: 'World Bank API integration provides real-time GDP growth, inflation, unemployment, FDI, debt, and trade data. Auto-refreshing, never stale.', metric: '8 indicators' },
              { icon: Shield, title: 'Risk Intelligence', desc: 'Real risk evaluation based on credit ratings, HDI scores, and sovereign scores — not arbitrary numbers. Applied consistently across all views.', metric: 'Data-driven' },
              { icon: Scale, title: 'Institutional-Grade Analysis', desc: 'Our AI intelligence engine configured as a senior economist. Follows Pyramid Principle, MECE logic, and quantifies every assertion.', metric: 'Institutional standard' },
              { icon: FileText, title: 'Investment-Grade PDFs', desc: '30-40 page investment-grade reports with cover page, TOC, charts (matplotlib), illustrations (Gemini), and numbered references. Board-ready.', metric: '38+ pages' },
              { icon: TrendingUp, title: 'Historical Trends', desc: '6-year economic trend charts for every country. Track GDP growth, inflation, unemployment trajectories. Identify inflection points.', metric: '6-year data' },
              { icon: Cpu, title: 'AI Studio', desc: 'Generate video briefings (Sora 2), audio narrations (OpenAI TTS), and infographics (Gemini). Multi-modal intelligence delivery.', metric: '4 AI tools' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-5 bg-[#f8f9ff] rounded-xl border border-[#c3c6d7]/20">
                  <div className="flex items-center justify-between mb-3">
                    <Icon className="w-5 h-5 text-[#00A651]" />
                    <span className="text-[10px] font-bold text-[#00A651] bg-[#00A651]/10 px-2 py-0.5 rounded">{item.metric}</span>
                  </div>
                  <h4 className="text-sm font-bold text-[#0b1c30] mb-1">{item.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ROI Calculator */}
        <section className="bg-[#1B2A3D] text-white rounded-xl shadow-sm p-8">
          <h2 className="text-xl font-bold tracking-tight mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#00A651]" /> Return on Investment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-bold text-[#00A651] mb-3">Traditional Approach</h3>
              <div className="space-y-2">
                {[
                  { label: 'Cost per country report', value: '$75,000 - $200,000' },
                  { label: 'Time to deliver', value: '6-12 weeks' },
                  { label: 'Team required', value: '3-5 analysts + partner' },
                  { label: 'Countries per year', value: '2-4 (budget constrained)' },
                  { label: 'Sectors per report', value: '1 (single sector focus)' },
                  { label: 'Data currency', value: 'Stale within weeks' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between text-xs py-1.5 border-b border-white/10">
                    <span className="text-white/60">{item.label}</span>
                    <span className="text-white/40">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#00A651] mb-3">With Sovereign IQ</h3>
              <div className="space-y-2">
                {[
                  { label: 'Cost per country report', value: 'Near zero (AI generation)' },
                  { label: 'Time to deliver', value: 'Minutes' },
                  { label: 'Team required', value: 'Self-service (any user)' },
                  { label: 'Countries per year', value: 'Unlimited (195 available)' },
                  { label: 'Sectors per report', value: '50 (multi-sector per country)' },
                  { label: 'Data currency', value: 'Real-time (World Bank API)' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between text-xs py-1.5 border-b border-white/10">
                    <span className="text-white/60">{item.label}</span>
                    <span className="font-bold text-[#00A651]">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Competitive Differentiation */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#00A651]" /> What Makes Sovereign IQ Different
          </h2>
          <div className="space-y-3">
            {[
              { title: 'Sector-Independent Architecture', desc: 'Unlike traditional reports locked to one industry, our blueprints work across 115 sectors. Analyze Kenya for waste management AND renewable energy AND fintech — same country, three different strategic lenses, each with tailored PESTLE, market opportunity, and business model analysis.' },
              { title: 'Pyramid Principle Built In', desc: 'Every section leads with the answer, supports with structured MECE logic, and backs claims with hard data. This isn\'t generic AI text — it\'s formatted the way investment committees expect to receive information.' },
              { title: 'Real Data, Not Hallucinations', desc: 'Risk evaluation uses actual credit ratings (Fitch, S&P, Moody\'s), HDI scores, and sovereign metrics extracted from the AI-generated analysis. World Bank API provides live economic indicators. The platform never invents numbers.' },
              { title: 'Multi-Modal Intelligence', desc: 'Beyond text, generate video briefings (Sora 2), audio narrations (OpenAI TTS), infographics (Gemini), and data visualizations (matplotlib). Deliver intelligence in whatever format your audience prefers.' },
              { title: 'Institutional Memory', desc: 'Every blueprint, every section, every metric is stored and searchable. The platform becomes your organization\'s living country intelligence library — always up to date, always available, always consistent.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-[#f8f9ff] rounded-lg">
                <div className="w-6 h-6 rounded-full bg-[#00A651] flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#0b1c30] mb-1">{item.title}</h4>
                  <p className="text-xs text-[#434655] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="text-center py-8 text-[10px] text-[#434655]">
          Sovereign IQ — Transforming how the world understands country risk and opportunity
        </div>
      </div>
      </div>
    </div>
  );
}

export default UseCasesPage;
